import { Box, ClickAwayListener, Paper, Popper, TextField } from '@mui/material';
import { GridRenderCellParams, GridRowId, GridValidRowModel } from '@mui/x-data-grid-pro';
import { formatNumber, NumberFormatPreset } from '@procureai/ui-mui';
import { useState, useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TextFieldCellType } from '../../DataGridCustom.types';
import { DefaultDataEditGridConfig } from '~/components/DataGridCustom';
import { defaultTextFieldProps } from '~/components/DataGridCustom/common/defaults';

interface TextAreaCellProps<R extends GridValidRowModel> extends GridRenderCellParams<R>, DefaultDataEditGridConfig {
  preset?: NumberFormatPreset;
  type?: TextFieldCellType;
}

export const TextAreaCell = <R extends GridValidRowModel & { id: GridRowId } = { id: GridRowId }>({
  colDef,
  getRowIndex,
  pathPrefix,
  preset,
  row,
  type = TextFieldCellType.TEXT,
  languageNamespace,
  keyPrefix,
  textFieldProps,
}: TextAreaCellProps<R>) => {
  const { t: tPrefix } = useTranslation(languageNamespace, { keyPrefix });
  const p = [...(pathPrefix ?? []), getRowIndex?.(row.id), colDef.field];

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>();
  const [open, setOpen] = useState(false);

  const handleRef = useCallback((el: HTMLElement | null) => {
    setAnchorEl(el);
  }, []);

  return (
    <Controller
      name={p.join('.')}
      defaultValue=""
      render={({ field, fieldState }) => {
        const value = field.value || '';
        return (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
            }}
          >
            <TextField
              {...field}
              label={tPrefix(colDef.field)}
              disabled={open}
              sx={{
                visibility: open ? 'hidden' : 'visible',
              }}
              onClick={() => {
                setOpen((prev) => !prev);
              }}
              value={preset ? formatNumber(preset, Number(value)) : value}
              {...defaultTextFieldProps}
              {...textFieldProps}
              slotProps={{
                input: {
                  readOnly: true,
                },

                inputLabel: { shrink: true },
              }}
            />
            <>
              <div
                ref={handleRef}
                style={{
                  height: 1,
                  width: colDef.computedWidth - 20,
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                }}
              />
              {anchorEl && (
                <ClickAwayListener
                  mouseEvent="onMouseDown"
                  touchEvent="onTouchStart"
                  onClickAway={() => {
                    setOpen(false);
                  }}
                >
                  <Popper open={open} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 7 }}>
                    <Paper elevation={1} sx={{ minWidth: colDef.computedWidth - 20 }}>
                      <TextField
                        {...field}
                        multiline
                        rows={4}
                        minRows={2}
                        label={tPrefix(colDef.field)}
                        sx={{
                          textarea: {
                            resize: 'both',
                            maxWidth: '500px',
                            maxHeight: '200px',
                          },
                        }}
                        type={type}
                        error={!!fieldState.error}
                        value={preset ? formatNumber(preset, Number(value)) : value}
                        {...defaultTextFieldProps}
                        {...textFieldProps}
                        slotProps={{
                          input: {
                            sx: {
                              minHeight: '30px',
                            },
                          },
                        }}
                      />
                    </Paper>
                  </Popper>
                </ClickAwayListener>
              )}
            </>
          </Box>
        );
      }}
    />
  );
};
